import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, FileText, Users, Activity, LogOut, UserCog, Globe, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '../contexts/AuthContext';

const AdminLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const navItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/applications', icon: FileText, label: 'Applications' },
    { path: '/admin/risk-assessments', icon: Shield, label: 'Risk Assessments' },
    { path: '/admin/affiliates', icon: Users, label: 'Affiliates' },
    { path: '/admin/landing-pages', icon: Globe, label: 'Landing Pages' },
    { path: '/admin/users', icon: UserCog, label: 'User Management' },
    { path: '/admin/activity', icon: Activity, label: 'Activity Logs' },
  ];

  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      {/* Sidebar — Dark Brand */}
      <aside className="fixed top-0 left-0 h-full w-56 bg-[#232323] border-r border-white/10">
        {/* Gradient accent line on top */}
        <div className="h-[3px] bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4]"></div>
        
        <div className="p-5">
          <div className="flex items-center gap-2.5 mb-8">
            <img src="/logo-light.png" alt="Players Health" className="h-8" />
          </div>
          
          <nav className="space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-md transition-all ${
                    isActive 
                      ? 'bg-[#7B6FE8]/15 text-[#7B6FE8] font-semibold' 
                      : 'text-white/70 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <Icon className={`h-5 w-5 ${isActive ? 'text-[#7B6FE8]' : 'text-white/50'}`} />
                  <span className="text-sm font-medium">{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-5 border-t border-white/10">
          <div className="mb-3">
            <p className="text-xs text-white/40">Signed in as</p>
            <p className="text-sm font-medium text-white/80 truncate">{user?.email}</p>
          </div>
          <Button
            onClick={handleLogout}
            data-testid="logout-btn"
            variant="outline"
            className="w-full text-white/70 border-white/15 hover:bg-white/5 hover:text-white text-sm font-medium bg-transparent"
          >
            <LogOut className="mr-2 h-4 w-4 text-white/50" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="ml-56">
        <main className="p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
